'use client'

import React, { createContext, useContext, useState, ReactNode } from 'react'
import { signIn, signOut } from 'next-auth/react'
import { useSession } from '@/context/SessionContext'
import { User } from 'next-auth'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  error: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { session, status } = useSession()
  const [error, setError] = useState<string | null>(null)

  const login = async (email: string, password: string) => {
    try {
      setError(null)
      const result = await signIn('credentials', {
        redirect: false,
        email,
        password,
      })

      if (result?.error) {
        setError(result.error)
        throw new Error(result.error)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during login')
      throw err
    }
  }

  const logout = async () => {
    try {
      setError(null)
      await signOut({ redirect: false })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during logout')
      throw err
    }
  }

  const value = {
    user: session?.user || null,
    loading: status === 'loading',
    login,
    logout,
    error,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}